import {
	Dispatch,
	FunctionComponent,
	ReactNode,
	SetStateAction,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import {
	GeoNode,
	SubscriptionType,
} from 'domain/subscription/interfaces/Subscription';
import { Moment } from 'moment';
import moment from 'moment';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';
import { SelectOptionType } from '@montel/montelpro-shared-components-v2';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';

export interface BundleFormState {
	productId: string;
	description?: string;
	seats: number;
	endDate?: Moment;
	companyId: number | null;
	type: SubscriptionType;
	areas: string[];
	addons: string[];
}

export type BundleFormContextType = {
	availableApplications: SelectOptionType[];
	availableAreas: GeoNode[];
	availableAddons: SelectOptionType[];
	bundleFormState: BundleFormState;
	setBundleFormState: Dispatch<SetStateAction<BundleFormState>>;
	resetFormState: () => void;
	setCompanyId: (companyId: number) => void;
	setFormStateFromBundle: (bundle: SubscriptionBundle) => void;
};

const defaultFormState: BundleFormState = {
	productId: '',
	description: '',
	seats: 1,
	endDate: undefined,
	companyId: null,
	type: SubscriptionType.Paid,
	areas: [],
	addons: [],
};

export const BundleFormContext = createContext<BundleFormContextType | null>(
	null,
);

const BundleFormContextProvider: FunctionComponent<{
	children: ReactNode;
}> = ({ children }) => {
	const [bundleFormState, setBundleFormState] =
		useState<BundleFormState>(defaultFormState);

	const [availableAreas, setAvailableAreas] = useState<GeoNode[]>([]);
	const [availableAddons, setAvailableAddons] = useState<SelectOptionType[]>(
		[],
	);

	const { permissionOptions } = useGlobalContext();

	if (!permissionOptions) {
		return <></>;
	}

	useEffect(() => {
		const permissionSet = permissionOptions.find(
			(po) => po.applicationId === bundleFormState.productId,
		);

		const relevantAddons = permissionSet?.addons.filter(
			(addon) =>
				addon.applicationId === bundleFormState.productId &&
				addon.availableForBundle,
		);

		setAvailableAreas(permissionSet?.areas || []);
		setAvailableAddons(
			relevantAddons?.map((addon) => ({
				value: addon.code,
				label: addon.name,
			})) || [],
		);
	}, [bundleFormState.productId, permissionOptions]);

	const availableApplications: SelectOptionType[] = permissionOptions.map(
		(app) => ({
			value: app.applicationId,
			label: app.applicationName,
		}),
	);

	const resetFormState = () => {
		setBundleFormState(defaultFormState);
	};

	const setCompanyId = useCallback((companyId: number) => {
		setBundleFormState((prevState) => ({ ...prevState, companyId }));
	}, []);

	const setFormStateFromBundle = (bundle: SubscriptionBundle) => {
		setBundleFormState({
			productId: bundle.productId,
			description: bundle.description ?? '',
			seats: bundle.seats,
			endDate: bundle.endDate ? moment(bundle.endDate) : undefined,
			companyId: bundle.companyId,
			type: bundle.type,
			areas: bundle.permissions.countries,
			addons:
				bundle.permissions.applicationPermissions.find(
					(ap) => ap.applicationId === bundle.productId,
				)?.addons || [],
		});
	};

	return (
		<BundleFormContext.Provider
			value={{
				availableAreas,
				availableApplications,
				availableAddons,
				bundleFormState,
				setBundleFormState,
				resetFormState,
				setFormStateFromBundle,
				setCompanyId,
			}}
		>
			{children}
		</BundleFormContext.Provider>
	);
};

export function useBundleFormContext() {
	const context = useContext(BundleFormContext);
	if (!context) {
		throw new Error(
			'useBundleFormContext must be used within BundleFormContextProvider',
		);
	}
	return context;
}

export default BundleFormContextProvider;
